import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/api';
import LteBandSection from '../lte-band-section';
import '../port-create-panel/port-create-panel.css';

/**
 * PortEditPanel — slide-in панель редактирования сетевого порта.
 * Повторяет дизайн PortCreatePanel (pcp-* классы), но:
 *   - предзаполняется данными выбранного порта (initialData);
 *   - при сабмите отправляет только изменённые поля (дельту);
 *   - подключение Кракен показывается как read-only.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = ({ title }) => (
  <svg
    className="pcp__info-icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
  >
    <title>{title}</title>
    <circle cx="6" cy="6" r="5.25" stroke="currentColor" strokeWidth="1" />
    <path d="M6 5.5V8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <circle cx="6" cy="3.75" r="0.6" fill="currentColor" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 7.5L6 5L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SelectChevron = () => (
  <svg className="pcp__select-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== SELECT OPTIONS ====================

const NET_MODE_OPTIONS = [
  { value: 'lte', label: 'LTE' },
  { value: 'umts', label: 'UMTS (3G)' },
];

const RECONNECT_TYPE_OPTIONS = [
  { value: 'time', label: 'По времени' },
  { value: 'link', label: 'Ссылка' },
  { value: 'time_link', label: 'Время + ссылка' },
];

/** Условия переподключения модема в Kraken API (поле reconnect_event) */
const KRAKEN_RECONNECT_EVENT_UI = [
  { token: 'not_provider', label: 'Нет подключения к оператору' },
  { token: 'not_inet', label: 'Нет подключения к интернету' },
  { token: 'unique_ip', label: 'Не уникальный внешний IP' },
];

const UI_RECONNECT_EVENT_TOKENS = KRAKEN_RECONNECT_EVENT_UI.map((x) => x.token);

const normalizeReconnectEventList = (raw) => {
  const arr = Array.isArray(raw) ? [...raw] : [];
  const rest = arr.filter((t) => !UI_RECONNECT_EVENT_TOKENS.includes(t));
  const picked = UI_RECONNECT_EVENT_TOKENS.filter((t) => arr.includes(t));
  return [...rest, ...picked];
};

// ==================== SUB-COMPONENTS ====================

const NumberField = ({ label, required, value, onChange, onKeyDown, min, max, step = 1, infoTitle }) => {
  const handleIncrement = () => {
    const num = Number(value) || 0;
    const next = Math.min(num + step, max ?? Infinity);
    onChange(next);
  };

  const handleDecrement = () => {
    const num = Number(value) || 0;
    const next = Math.max(num - step, min ?? -Infinity);
    onChange(next);
  };

  return (
    <div className="pcp__field">
      <div className="pcp__label">
        {required && <span className="pcp__label-required">*</span>}
        <span className="pcp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className="pcp__number-wrap">
        <input
          type="number"
          className="pcp__input pcp__input--number"
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { onChange(''); return; }
            let num = Number(raw);
            if (isNaN(num)) return;
            if (min !== undefined && num < min) num = min;
            if (max !== undefined && num > max) num = max;
            onChange(num);
          }}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
          aria-label={label}
        />
        <div className="pcp__number-arrows">
          <button
            type="button"
            className="pcp__number-arrow pcp__number-arrow--up"
            onClick={handleIncrement}
            tabIndex={-1}
            aria-label="Увеличить"
          >
            <ChevronUpIcon />
          </button>
          <button
            type="button"
            className="pcp__number-arrow pcp__number-arrow--down"
            onClick={handleDecrement}
            tabIndex={-1}
            aria-label="Уменьшить"
          >
            <ChevronDownIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange, disabled = false }) => (
  <div className={`pcp__toggle-row${disabled ? ' pcp__toggle-row--disabled' : ''}`}>
    <span className="pcp__toggle-label">{label}</span>
    <button
      type="button"
      className={`pcp__toggle${checked ? ' pcp__toggle--on' : ''}`}
      onClick={() => { if (!disabled) onChange(!checked); }}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={label}
      tabIndex={disabled ? -1 : 0}
    >
      <span className="pcp__toggle-dot" />
    </button>
  </div>
);

/**
 * Custom select / dropdown (pcp-styled).
 */
const SelectField = ({
  label,
  required,
  placeholder,
  value,
  onChange,
  options = [],
  loading = false,
  disabled = false,
  error,
  searchable = true,
  infoTitle,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open, searchable]);

  const selectedOption = options.find(o => o.value === value);
  const displayText = selectedOption ? selectedOption.label : null;

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const triggerClasses = [
    'pcp__select-trigger',
    !displayText ? 'pcp__select-trigger--placeholder' : '',
    disabled ? 'pcp__select-trigger--disabled' : '',
    error ? 'pcp__select-trigger--error' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="pcp__field">
      <div className="pcp__label">
        {required && <span className="pcp__label-required">*</span>}
        <span className="pcp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className={`pcp__select-wrap${open ? ' pcp__select-wrap--open' : ''}`} ref={wrapRef}>
        <button
          type="button"
          className={triggerClasses}
          onClick={() => { if (!disabled) setOpen(!open); }}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        >
          <span className="pcp__select-value">
            {displayText || placeholder || 'Выберите...'}
          </span>
          <SelectChevron />
        </button>

        {open && (
          <div className="pcp__select-dropdown" role="listbox">
            {searchable && (
              <input
                ref={searchRef}
                type="text"
                className="pcp__select-search"
                placeholder="Поиск..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {loading && <div className="pcp__select-empty">Загрузка...</div>}
            {!loading && filtered.length === 0 && (
              <div className="pcp__select-empty">Ничего не найдено</div>
            )}
            {!loading && filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`pcp__select-option${opt.value === value ? ' pcp__select-option--active' : ''}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <span className="pcp__field-error">{error}</span>}
    </div>
  );
};

// ==================== HELPERS ====================

/** Нормализация osfp значения в число | null */
const normalizeOsfp = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'object' && raw !== null) return raw.id || null;
  if (typeof raw === 'string') { const n = parseInt(raw, 10); return isNaN(n) ? null : n; }
  return typeof raw === 'number' ? raw : null;
};

/** Нормализация safety_buffer: сервер хранит 0..1, UI показывает 0..100 */
const bufferToUI = (v) => {
  if (v === undefined || v === null) return 100;
  const n = Number(v);
  if (isNaN(n)) return 100;
  return n <= 1 ? Math.round(n * 100) : Math.round(n);
};

const bufferToAPI = (v) => {
  const n = Number(v);
  if (isNaN(n)) return 1.0;
  return n > 1 ? n / 100 : n;
};

// ==================== MAIN COMPONENT ====================

const PortEditPanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  onBandApplied = null,
  submitting = false,
  initialData = null,
  krakenConnections = [],
  modemTypes = [],
  tcpFingerprints = [],
  loadingTcpFingerprints = false,
  serverUsers = [],
}) => {
  const [formData, setFormData] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState({});
  const [errors, setErrors] = useState({});
  const [krakenLiveLoading, setKrakenLiveLoading] = useState(false);
  const [krakenLiveError, setKrakenLiveError] = useState(null);
  const [krakenLiveOk, setKrakenLiveOk] = useState(false);
  const liveFetchGenRef = useRef(0);

  // ==================== INIT FORM ON OPEN ====================
  useEffect(() => {
    if (visible && initialData) {
      const snapshot = {
        name: initialData.name || '',
        ifname: initialData.ifname || '',
        type: initialData.type,
        active: Boolean(initialData.active ?? true),
        capacity_speed_mbps: initialData.capacity_speed_mbps || 26,
        max_tcp_connections: initialData.max_tcp_connections ?? 1000,
        safety_buffer: bufferToUI(initialData.safety_buffer),
        monitoring_interval: initialData.monitoring_interval || 3,
        net_mode: initialData.net_mode || 'lte',
        reconnect_type: initialData.reconnect_type || 'time_link',
        reconnect_interval: initialData.reconnect_interval || 240,
        reconnect_min: initialData.reconnect_min ?? '',
        mtu: initialData.mtu ?? 0,
        reconnect_event: normalizeReconnectEventList(initialData.reconnect_event),
        is_osfp: Boolean(initialData.is_osfp ?? false),
        osfp: normalizeOsfp(initialData.osfp),
        users: initialData.users || [],
      };
      setFormData({ ...snapshot });
      setInitialSnapshot({ ...snapshot });
      setErrors({});
    }
    if (!visible) {
      setFormData({});
      setInitialSnapshot({});
      setErrors({});
      setKrakenLiveLoading(false);
      setKrakenLiveError(null);
      setKrakenLiveOk(false);
    }
  }, [visible, initialData]);

  // Актуальный reconnect_event из Kraken при открытии панели
  useEffect(() => {
    if (!visible || !initialData?.server_id) {
      return;
    }
    const gen = liveFetchGenRef.current + 1;
    liveFetchGenRef.current = gen;
    setKrakenLiveLoading(true);
    setKrakenLiveError(null);
    setKrakenLiveOk(false);

    const run = async () => {
      try {
        const res = await apiService.getKrakenPortLiveModem(initialData.server_id);
        if (gen !== liveFetchGenRef.current) {
          return;
        }
        const raw = res?.data?.reconnect_event;
        const normalized = normalizeReconnectEventList(Array.isArray(raw) ? raw : []);
        setFormData((prev) => ({ ...prev, reconnect_event: normalized }));
        setInitialSnapshot((prev) => ({ ...prev, reconnect_event: normalized }));
        setKrakenLiveOk(true);
      } catch (err) {
        if (gen !== liveFetchGenRef.current) {
          return;
        }
        const detail = err?.response?.data?.detail;
        setKrakenLiveError(
          typeof detail === 'string' ? detail : (err?.message || 'Ошибка запроса к Kraken'),
        );
      } finally {
        if (gen === liveFetchGenRef.current) {
          setKrakenLiveLoading(false);
        }
      }
    };
    run();
  }, [visible, initialData?.server_id]);

  // ==================== HANDLERS ====================
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handleReconnectEventTokenChange = useCallback((token, enabled) => {
    setFormData((prev) => {
      const cur = new Set(prev.reconnect_event || []);
      if (enabled) {
        cur.add(token);
      } else {
        cur.delete(token);
      }
      return { ...prev, reconnect_event: normalizeReconnectEventList([...cur]) };
    });
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!formData.name || !formData.name.trim()) e.name = 'Введите название порта';
    if (!formData.ifname) e.ifname = 'Введите сетевой интерфейс';
    if (!formData.type && formData.type !== 0) e.type = 'Выберите тип модема';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!onSubmit) return;

    // Вычисляем дельту
    const delta = {};
    Object.keys(formData).forEach(key => {
      const cur = JSON.stringify(formData[key]);
      const init = JSON.stringify(initialSnapshot[key]);
      if (cur !== init) {
        delta[key] = formData[key];
      }
    });

    // Нормализация
    if ('safety_buffer' in delta) {
      delta.safety_buffer = bufferToAPI(delta.safety_buffer);
    }
    if ('capacity_speed_mbps' in delta) {
      delta.capacity_speed_mbps = Number(delta.capacity_speed_mbps);
    }
    if ('max_tcp_connections' in delta) {
      delta.max_tcp_connections = parseInt(delta.max_tcp_connections, 10);
    }
    if ('name' in delta) {
      delta.name = delta.name.trim();
    }
    if ('type' in delta) {
      delta.type = Number(delta.type);
    }

    // OSFP в Kraken только если реально меняли — иначе бэкенд гоняет полный rewrite модема
    if ('is_osfp' in delta || 'osfp' in delta) {
      delta.is_osfp = formData.is_osfp;
      delta.osfp = formData.is_osfp ? (formData.osfp || null) : null;
    }

    if (Object.keys(delta).length === 0) {
      return;
    }

    onSubmit(delta);
  }, [formData, initialSnapshot, validate, onSubmit]);

  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // ==================== DERIVED OPTIONS ====================

  const connectionLabel = (() => {
    if (!initialData?.connection_id) return 'Не указано';
    const conn = krakenConnections.find(c => c.id === initialData.connection_id);
    if (conn) return conn.alias ? `${conn.name} (${conn.alias})` : conn.name;
    return `ID: ${initialData.connection_id}`;
  })();

  const modemTypeOptions = modemTypes.length > 0
    ? modemTypes.map(t => ({ value: t.id, label: t.name || `Type ${t.id}` }))
    : [
        { value: 1, label: 'Huawei E3372h-153' },
        { value: 2, label: 'ZTE MF833R' },
        { value: 3, label: 'ZTE MF920RU' },
        { value: 4, label: 'Huawei E3372h-320' },
      ];

  const fingerprintOptions = tcpFingerprints.map(fp => ({
    value: fp.id,
    label: fp.name ? `${fp.name}${fp.modem ? ` (${fp.modem})` : ''}` : `ID: ${fp.id}`,
  }));

  const userOptions = serverUsers.map(u => ({
    value: u.id,
    label: u.name ? `${u.name} (${u.login})` : u.login,
  }));

  // ==================== EARLY RETURN ====================
  if (!visible) return null;

  return createPortal(
    <div
      className="pcp-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="pcp" role="dialog" aria-label="Редактирование сетевого порта">

        {/* ==================== HEADER ==================== */}
        <div className="pcp__header">
          <h2 className="pcp__title">редактирование сетевого порта</h2>
          <button
            type="button"
            className="pcp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="pcp__body">

          {/* --- Section: Основные настройки --- */}
          <div className="pcp__section">
            <div className="pcp__section-title">Основные настройки</div>

            {/* Row: Подключение Кракен (read-only) + Название порта */}
            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-text">Подключение Кракен</span>
                </div>
                <div className="pcp__select-trigger pcp__select-trigger--disabled">
                  <span className="pcp__select-value">{connectionLabel}</span>
                </div>
              </div>
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-required">*</span>
                  <span className="pcp__label-text">Название порта</span>
                </div>
                <input
                  type="text"
                  className={`pcp__input${errors.name ? ' pcp__input--error' : ''}`}
                  placeholder="Введите название"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Название порта"
                  aria-required="true"
                  autoComplete="off"
                />
                {errors.name && <span className="pcp__field-error">{errors.name}</span>}
              </div>
            </div>

            {/* Row: Сетевой интерфейс + Тип модема */}
            <div className="pcp__row">
              <div className="pcp__field">
                <div className="pcp__label">
                  <span className="pcp__label-required">*</span>
                  <span className="pcp__label-text">Сетевой интерфейс</span>
                </div>
                <input
                  type="text"
                  className={`pcp__input${errors.ifname ? ' pcp__input--error' : ''}`}
                  placeholder="Введите имя интерфейса"
                  value={formData.ifname || ''}
                  onChange={(e) => handleChange('ifname', e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Сетевой интерфейс"
                  aria-required="true"
                  autoComplete="off"
                />
                {errors.ifname && <span className="pcp__field-error">{errors.ifname}</span>}
              </div>
              <SelectField
                label="Тип модема"
                required
                placeholder="Выберите тип модема"
                value={formData.type}
                onChange={(v) => handleChange('type', v)}
                options={modemTypeOptions}
                loading={modemTypes.length === 0}
                error={errors.type}
              />
            </div>

            {/* Toggle: Активность порта */}
            <ToggleRow
              label="Активность порта"
              checked={formData.active ?? true}
              onChange={(v) => handleChange('active', v)}
            />
          </div>

          {/* --- Section: Характеристики производительности --- */}
          <div className="pcp__section">
            <div className="pcp__section-title">Характеристики производительности</div>

            <div className="pcp__row">
              <NumberField
                label="Скорость (емкость) в Мб/с"
                required
                value={formData.capacity_speed_mbps}
                onChange={(v) => handleChange('capacity_speed_mbps', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={1000}
                infoTitle="Максимальная пропускная способность порта в Мб/с"
              />
              <NumberField
                label="Макс. TCP соединений"
                value={formData.max_tcp_connections}
                onChange={(v) => handleChange('max_tcp_connections', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={10000}
                infoTitle="Максимальное количество одновременных TCP соединений"
              />
            </div>

            <div className="pcp__row">
              <NumberField
                label="Буфер безопасности (%)"
                required
                value={formData.safety_buffer}
                onChange={(v) => handleChange('safety_buffer', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={100}
              />
            </div>
          </div>

          {/* --- Section: Настройки мониторинга --- */}
          <div className="pcp__section pcp__section--grow">
            <div className="pcp__section-title">Настройки мониторинга</div>

            <div className="pcp__row">
              <NumberField
                label="Интервал мониторинга (сек)"
                required
                value={formData.monitoring_interval}
                onChange={(v) => handleChange('monitoring_interval', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={300}
                infoTitle="Интервал опроса статуса модема в секундах"
              />
              <SelectField
                label="Тип сети"
                required
                value={formData.net_mode}
                onChange={(v) => handleChange('net_mode', v)}
                options={NET_MODE_OPTIONS}
                searchable={false}
                infoTitle="Тип мобильной сети модема"
              />
            </div>

            <div className="pcp__row">
              <SelectField
                label="Тип переподключения модема"
                required
                value={formData.reconnect_type}
                onChange={(v) => handleChange('reconnect_type', v)}
                options={RECONNECT_TYPE_OPTIONS}
                searchable={false}
              />
              <NumberField
                label="Интервал переподключения модема (сек)"
                required
                value={formData.reconnect_interval}
                onChange={(v) => handleChange('reconnect_interval', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={3600}
              />
            </div>

            <div className="pcp__row">
              <NumberField
                label="Минимальный шаг (сек)"
                value={formData.reconnect_min}
                onChange={(v) => handleChange('reconnect_min', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={300}
              />
              <NumberField
                label="MTU (0 - по умолчанию)"
                required
                value={formData.mtu}
                onChange={(v) => handleChange('mtu', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={1500}
              />
            </div>

            {/* Toggle: Подмена TCP отпечатка */}
            <ToggleRow
              label="Использовать подмену TCP отпечатка"
              checked={formData.is_osfp ?? false}
              onChange={(v) => {
                handleChange('is_osfp', v);
                if (!v) handleChange('osfp', null);
              }}
            />

            {/* Select: TCP отпечаток (только при is_osfp) */}
            {formData.is_osfp && (
              <div className="pcp__row">
                <SelectField
                  label="TCP отпечаток"
                  placeholder="Выберите TCP отпечаток"
                  value={formData.osfp}
                  onChange={(v) => handleChange('osfp', v)}
                  options={fingerprintOptions}
                  loading={loadingTcpFingerprints}
                />
              </div>
            )}

            {/* Пользователи (multi-tag style via select) */}
            {userOptions.length > 0 && (
              <div className="pcp__row">
                <SelectField
                  label="Пользователи"
                  placeholder="Выберите пользователей"
                  value={formData.users && formData.users.length > 0 ? formData.users[0] : undefined}
                  onChange={(v) => {
                    const current = formData.users || [];
                    if (current.includes(v)) {
                      handleChange('users', current.filter(id => id !== v));
                    } else {
                      handleChange('users', [...current, v]);
                    }
                  }}
                  options={userOptions}
                  infoTitle="Пользователи с делегированным доступом к порту"
                />
              </div>
            )}

            <div className="pcp__subsection">
              <div className="pcp__subsection-title">Условия переподключения модема (Kraken)</div>
              {initialData?.server_id && krakenLiveLoading && (
                <p className="pcp__subsection-status" role="status">
                  Загрузка значений из Kraken…
                </p>
              )}
              {initialData?.server_id && !krakenLiveLoading && krakenLiveOk && (
                <p className="pcp__subsection-status pcp__subsection-status--ok" role="status">
                  Переключатели соответствуют данным Kraken (GET modem).
                </p>
              )}
              {initialData?.server_id && !krakenLiveLoading && krakenLiveError && (
                <p className="pcp__subsection-status pcp__subsection-status--err" role="alert">
                  Не удалось получить данные из Kraken: {krakenLiveError}. Ниже — значения из списка портов.
                </p>
              )}
              {!initialData?.server_id && (
                <p className="pcp__subsection-status pcp__subsection-status--err" role="note">
                  Нет server_id — синхронизация с Kraken недоступна.
                </p>
              )}
              {KRAKEN_RECONNECT_EVENT_UI.map(({ token, label }) => (
                <ToggleRow
                  key={token}
                  label={label}
                  checked={Boolean((formData.reconnect_event || []).includes(token))}
                  onChange={(v) => handleReconnectEventTokenChange(token, v)}
                  disabled={Boolean(initialData?.server_id && krakenLiveLoading)}
                />
              ))}
            </div>
          </div>

          <LteBandSection
            visible={visible}
            modemId={initialData?.server_id ?? initialData?.id ?? null}
            onApplied={onBandApplied}
          />
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="pcp__footer">
          <button
            type="button"
            className="pcp__btn pcp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="pcp__btn pcp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label="Сохранить"
            tabIndex={0}
          >
            {submitting ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PortEditPanel;
